import React, { useRef, useState } from 'react';
import { useTranslation } from '../../i18n/fallback';
import { formatBillDateParts, formatNumberTrimZeros } from '../../utils/billFormat';
import BillPrintControls from '../common/BillPrintControls';
import { getBillContainerStyle, getBillDimensions, printBillFromRef, THERMAL_PRINT_PADDING } from '../../utils/billPrintSizes';

const BILL_FOOTER = {
  phones: '0744841167, 0704737305, 0730117373',
  email: 'afghanart.af@gmail.com',
  address: 'چهارراهی صدارت، سرک وزارت داخله سابقه، مارکیت مطابع صنعتی جاوید، منزل دوم دوکان نمبر A2 14-15',
};

/** Exactly 20 line items on one A4 page so the sheet is filled with footer included. */
const PRESS_BILL_MAX_LINES = 20;
const LOGO_SRC = '/logo.jpeg';
const COMPANY_NAME = 'بیرق سازی افغان';

function parseLineNum(v) {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}

/** Show making unit price, legacy per-meter, or derived rate from totals when DB fields are zero. */
function formatMakingCell(line, t, isCustomerBill) {
  if (!line || line.__empty) return '';
  const mk = parseLineNum(line.making_unit_price ?? line.making_price);
  if (mk > 0) return `AFN ${formatNumberTrimZeros(mk, '0')}`;
  const perM = parseLineNum(line.per_meter_price);
  if (perM > 0) {
    return `${formatNumberTrimZeros(perM, '0')} ${t('printing.afnPerMeter')}`;
  }
  const selling = parseLineNum(line.selling_unit_price ?? line.selling_price);
  const meters = parseLineNum(line.total_meters);
  const sub = parseLineNum(line.line_total);
  const qty = parseLineNum(line.qty ?? line.job_qty);
  if (selling <= 0 && meters > 0 && sub > 0) {
    const implied = sub / meters;
    return `${formatNumberTrimZeros(implied, '0')} ${t('printing.afnPerMeter')}`;
  }
  if (isCustomerBill && selling <= 0 && qty > 0 && sub > 0) {
    return `AFN ${formatNumberTrimZeros(sub / qty, '0')}`;
  }
  return '—';
}

/**
 * @param {object} record - printing job with items
 * @param {'customer'|'internal'} billAudience - customer bill hides selling column and profit; internal shows full margin view
 */
const PrintablePressBill = ({ record, billAudience = 'internal' }) => {
  const { t } = useTranslation();
  const billRef = useRef(null);
  const [pageSize, setPageSize] = useState('A4');
  const isCustomerBill = billAudience === 'customer';

  if (!record) return null;

  const billDateParts = formatBillDateParts(record.job_date || record.purchase_date);
  const rawLines = Array.isArray(record.items)
    ? record.items
    : Array.isArray(record.purchase_items)
      ? record.purchase_items
      : [];
  const truncated = rawLines.length > PRESS_BILL_MAX_LINES;
  const filledLines = truncated ? rawLines.slice(0, PRESS_BILL_MAX_LINES) : [...rawLines];
  while (filledLines.length < PRESS_BILL_MAX_LINES) {
    filledLines.push({ __empty: true });
  }
  const lines = filledLines;

  const isA5 = pageSize === 'A5';
  const isThermal = pageSize === 'Thermal';
  const compact = isA5 || isThermal;
  // A4: keep rows tight so 20 lines + totals + footer fill one page without overflow.
  const thStyle = {
    border: '1px solid #0047AB',
    padding: isThermal ? '1px 2px' : isA5 ? '2px 3px' : '2px 3px',
    textAlign: 'center',
    fontSize: isThermal ? '7px' : isA5 ? '7.5px' : '8px',
    lineHeight: 1.1,
  };
  const tdStyle = {
    border: '1px solid #ddd',
    padding: isThermal ? '1px 2px' : isA5 ? '1px 2px' : '1px 3px',
    textAlign: 'center',
    fontSize: isThermal ? '7px' : isA5 ? '7.5px' : '8px',
    lineHeight: 1.1,
    height: isThermal ? '12px' : isA5 ? '11px' : undefined,
  };
  const tdTextRtl = { ...tdStyle, textAlign: 'right' };
  const thNarrow = { ...thStyle, width: compact ? '36px' : '46px', padding: '2px 1px' };
  const thMaking = {
    ...thStyle,
    width: compact ? '58px' : '68px',
    minWidth: compact ? '54px' : '64px',
    padding: '2px 2px',
  };
  const tdMaking = {
    ...tdStyle,
    width: compact ? '58px' : '68px',
    minWidth: compact ? '54px' : '64px',
    padding: '1px 2px',
  };
  const billGrandTotal = parseFloat(record.total_price || record.cost || 0);
  const billProfit = rawLines.reduce((sum, line) => {
    const q = parseLineNum(line.qty ?? line.job_qty);
    const mk = parseLineNum(line.making_unit_price ?? line.making_price);
    const sl = parseLineNum(line.selling_unit_price ?? line.selling_price);
    const sub = parseLineNum(line.line_total);
    if (q <= 0 || sl <= 0) return sum;
    const cost = mk > 0 ? q * mk : sub;
    return sum + (q * sl - cost);
  }, 0);

  const handlePrint = () => {
    printBillFromRef(billRef, pageSize);
  };

  const dims = getBillDimensions(pageSize);

  return (
    <div className="w-full flex flex-col items-center py-4 bg-gray-50 dark:bg-gray-900">
      <div className="w-full max-w-[210mm] mx-auto px-4 mb-4">
        <BillPrintControls
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          onPrint={handlePrint}
        />
      </div>
      <div
        ref={billRef}
        className="printable-bill bg-white shadow-lg mx-auto press-bill-a4"
        style={{
          ...getBillContainerStyle(pageSize),
          maxWidth: '100%',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <style>{`
          .press-bill-a4 {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          .press-bill-table-wrap {
            flex: 1 1 auto;
            display: flex;
            flex-direction: column;
            min-height: 0;
          }
          .press-bill-table {
            width: 100%;
            border-collapse: collapse;
            table-layout: fixed;
            flex: 1 1 auto;
            height: 100%;
          }
          .press-bill-table tbody tr {
            height: calc(100% / ${PRESS_BILL_MAX_LINES});
          }
          @media print {
            html, body {
              margin: 0 !important;
              padding: 0 !important;
              width: 100% !important;
              height: auto !important;
            }
            .no-print { display: none !important; }
            body * { visibility: hidden; }
            .printable-bill, .printable-bill * { visibility: visible; }
            .printable-bill {
              position: absolute !important;
              left: 0 !important;
              top: 0 !important;
              right: 0 !important;
              width: ${dims.width} !important;
              max-width: ${dims.width} !important;
              height: ${isThermal ? 'auto' : dims.height} !important;
              max-height: ${isThermal ? 'none' : dims.height} !important;
              min-height: ${dims.minHeight} !important;
              overflow: ${isThermal ? 'visible' : 'hidden'} !important;
              margin: 0 !important;
              padding: ${isThermal ? THERMAL_PRINT_PADDING : '0'} !important;
              box-shadow: none !important;
              background: #fff !important;
              page-break-inside: avoid !important;
              box-sizing: border-box !important;
              display: flex !important;
              flex-direction: column !important;
            }
            .press-bill-a4 .press-bill-table { font-size: ${isThermal ? '7px' : isA5 ? '7px' : '7.5px'}; }
            .press-bill-a4 .press-bill-table th,
            .press-bill-a4 .press-bill-table td {
              padding: ${isThermal ? '1px 2px' : isA5 ? '1px 2px' : '1px 2px'} !important;
            }
            .press-bill-a4 .press-bill-table th.press-bill-making-col,
            .press-bill-a4 .press-bill-table td.press-bill-making-col {
              min-width: 60px !important;
              padding-left: 2px !important;
              padding-right: 2px !important;
            }
            @page { size: ${dims.pageSize}; margin: 0; }
          }
        `}</style>

        <div
          className="flex items-stretch"
          style={{ height: isThermal ? '36px' : isA5 ? '40px' : '54px', flexShrink: 0 }}
        >
          <div
            className="flex items-center justify-center gap-1.5"
            style={{ width: '32%', backgroundColor: '#0047AB', color: 'white', padding: '2px' }}
          >
            <img
              src={LOGO_SRC}
              alt={COMPANY_NAME}
              style={{
                width: isThermal ? 24 : isA5 ? 28 : 38,
                height: isThermal ? 24 : isA5 ? 28 : 38,
                borderRadius: '50%',
                objectFit: 'cover',
                border: '2px solid #fff',
                background: '#fff',
              }}
            />
          </div>
          <div
            className="flex items-center justify-center"
            style={{ width: '43%', backgroundColor: '#FFD700', padding: '2px 4px' }}
          >
            <h1
              style={{
                fontSize: isThermal ? '11px' : isA5 ? '12px' : '16px',
                fontWeight: 'bold',
                margin: 0,
                lineHeight: 1.15,
              }}
              dir="rtl"
            >
              {COMPANY_NAME}
            </h1>
          </div>
          <div
            className="flex items-center justify-center"
            style={{ width: '25%', backgroundColor: '#fff', position: 'relative' }}
          >
            <div
              style={{
                position: 'absolute',
                left: 0,
                top: '28%',
                transform: 'translateY(-50%)',
                backgroundColor: '#0047AB',
                color: 'white',
                padding: isThermal ? '2px 6px' : isA5 ? '2px 8px' : '3px 9px',
                fontWeight: 'bold',
                fontSize: isThermal ? '8px' : isA5 ? '8px' : '9px',
                clipPath: 'polygon(0 0, 100% 0, 85% 50%, 100% 100%, 0 100%)',
              }}
              dir="rtl"
            >
              {t('printing.billNumber')}
            </div>
            <div
              style={{
                position: 'absolute',
                bottom: isThermal ? '2px' : isA5 ? '3px' : '5px',
                right: isThermal ? '4px' : '6px',
                fontWeight: 'bold',
                color: '#0047AB',
                fontSize: isThermal ? '9px' : isA5 ? '9px' : '11px',
              }}
            >
              #{record.bill_number || record.id}
            </div>
          </div>
        </div>

        <div
          style={{
            padding: isThermal ? '3px 6px' : isA5 ? '3px 8px' : '4px 10px',
            backgroundColor: '#f8f9fa',
            flexShrink: 0,
          }}
          dir="rtl"
        >
          <div
            style={{
              fontSize: isThermal ? '7px' : isA5 ? '7.5px' : '9px',
              marginBottom: '1px',
              borderBottom: '1px dotted #999',
              paddingBottom: '1px',
            }}
          >
            <span style={{ fontWeight: 'bold' }}>{t('common.date')}:</span>
            <span style={{ marginRight: '8px' }}>
              {billDateParts
                ? `${billDateParts.year}/${billDateParts.month}/${billDateParts.day}`
                : '-'}
            </span>
          </div>
          <div
            style={{
              fontSize: isThermal ? '7px' : isA5 ? '7.5px' : '9px',
              borderBottom: '1px dotted #999',
              paddingBottom: '1px',
            }}
          >
            <span style={{ fontWeight: 'bold' }}>{t('printing.printer')}:</span>
            <span style={{ marginRight: '8px' }}>
              {record.printer_name || record.supplier_name || '-'}
            </span>
          </div>
        </div>

        <div
          className="press-bill-table-wrap"
          style={{ padding: isThermal ? '0 4px' : isA5 ? '0 6px' : '0 8px' }}
        >
          <table className="press-bill-table" style={{ marginTop: '2px' }}>
            <thead>
              <tr style={{ backgroundColor: '#0047AB', color: 'white' }}>
                <th style={{ ...thStyle, width: compact ? '18px' : '22px' }} dir="rtl">
                  نمبر
                </th>
                <th style={{ ...thStyle, textAlign: 'right', minWidth: '48px' }} dir="rtl">
                  {t('printing.flagName')}
                </th>
                <th style={thNarrow} dir="rtl">
                  {t('printing.size')}
                </th>
                <th style={thNarrow} dir="rtl">
                  {t('printing.qty')}
                </th>
                <th className="press-bill-making-col" style={thMaking} dir="rtl">
                  {isCustomerBill ? t('printing.makingRateColumn') : t('printing.makingUnitPrice')}
                </th>
                {!isCustomerBill ? (
                  <th style={thNarrow} dir="rtl">
                    {t('printing.sellingUnitPrice')}
                  </th>
                ) : null}
                <th style={thNarrow} dir="rtl">
                  {t('printing.lineSubtotal')}
                </th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line, idx) => {
                const empty = Boolean(line.__empty);
                const sub = parseLineNum(line.line_total);
                const qtyVal = line.qty ?? line.job_qty;
                const qtyDisplay = empty
                  ? ''
                  : qtyVal != null && qtyVal !== ''
                    ? formatNumberTrimZeros(qtyVal)
                    : '-';
                const slNum = parseLineNum(line.selling_unit_price ?? line.selling_price);
                const sellingDisplay =
                  !empty && !isCustomerBill && slNum > 0
                    ? `AFN ${formatNumberTrimZeros(slNum, '0')}`
                    : !empty && !isCustomerBill && parseLineNum(line.per_meter_price) > 0
                      ? `AFN ${formatNumberTrimZeros(line.per_meter_price, '0')}`
                      : empty
                        ? ''
                        : null;
                return (
                  <tr
                    key={`print-line-${idx}`}
                    style={{ backgroundColor: idx % 2 === 0 ? '#f8f9fa' : '#fff' }}
                  >
                    <td style={{ ...tdStyle, color: '#0047AB', fontWeight: 'bold' }}>{idx + 1}</td>
                    <td style={{ ...tdTextRtl, wordBreak: 'break-word' }} dir="rtl">
                      {empty ? '' : line.flag_name || line.item_name || '-'}
                    </td>
                    <td style={tdStyle}>{empty ? '' : line.size || line.flag_size || '-'}</td>
                    <td style={tdStyle}>{qtyDisplay}</td>
                    <td className="press-bill-making-col" style={tdMaking}>
                      {formatMakingCell(line, t, isCustomerBill)}
                    </td>
                    {!isCustomerBill ? (
                      <td style={tdStyle}>{sellingDisplay || (empty ? '' : '—')}</td>
                    ) : null}
                    <td style={{ ...tdStyle, fontWeight: 'bold' }}>
                      {empty ? '' : `AFN ${formatNumberTrimZeros(sub, '0')}`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {truncated ? (
            <p
              style={{ fontSize: '7px', color: '#666', marginTop: '2px', textAlign: 'center' }}
              dir="ltr"
            >
              {t('printing.pressBillLinesTruncated', {
                shown: PRESS_BILL_MAX_LINES,
                total: rawLines.length,
              })}
            </p>
          ) : null}
        </div>

        <div
          style={{
            padding: isThermal ? '3px 6px' : isA5 ? '4px 8px' : '5px 8px',
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: compact ? '4px' : '6px',
            flexShrink: 0,
          }}
          dir="rtl"
        >
          <div
            style={{
              border: '2px solid #0047AB',
              padding: compact ? '2px 8px' : '4px 12px',
              clipPath: 'polygon(0 0, 100% 0, 90% 50%, 100% 100%, 0 100%)',
            }}
          >
            <div style={{ fontSize: compact ? '8px' : '10px', fontWeight: 'bold', textAlign: 'center' }}>
              {t('printing.grandTotal')}
            </div>
            <div style={{ fontSize: compact ? '10px' : '12px', fontWeight: 'bold', textAlign: 'center' }}>
              {formatNumberTrimZeros(billGrandTotal, '0')}
            </div>
          </div>
          {!isCustomerBill ? (
            <div
              style={{
                border: '2px solid #228B22',
                padding: compact ? '2px 8px' : '4px 12px',
                clipPath: 'polygon(0 0, 100% 0, 90% 50%, 100% 100%, 0 100%)',
              }}
            >
              <div style={{ fontSize: compact ? '8px' : '10px', fontWeight: 'bold', textAlign: 'center' }}>
                {t('printing.totalProfit')}
              </div>
              <div style={{ fontSize: compact ? '10px' : '12px', fontWeight: 'bold', textAlign: 'center' }}>
                {formatNumberTrimZeros(billProfit, '0')}
              </div>
            </div>
          ) : null}
          <div style={{ minWidth: compact ? '100px' : '130px' }}>
            <div
              style={{
                fontSize: compact ? '7px' : '8.5px',
                marginBottom: '2px',
                borderBottom: '1px dotted #999',
                paddingBottom: '1px',
              }}
            >
              <span style={{ fontWeight: 'bold' }}>{t('purchases.paid')}:</span>
              <span style={{ marginRight: '8px' }}>
                {formatNumberTrimZeros(parseFloat(record.total_paid || 0), '0')}
              </span>
            </div>
            <div
              style={{
                fontSize: compact ? '7px' : '8.5px',
                borderBottom: '1px dotted #999',
                paddingBottom: '1px',
              }}
            >
              <span style={{ fontWeight: 'bold' }}>{t('purchases.remaining')}:</span>
              <span style={{ marginRight: '8px' }}>
                {formatNumberTrimZeros(parseFloat(record.remaining_amount || 0), '0')}
              </span>
            </div>
          </div>
        </div>

        <div
          style={{
            backgroundColor: '#FFD700',
            padding: isThermal ? '2px 4px' : isA5 ? '3px 6px' : '4px 8px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '4px',
            flexShrink: 0,
            marginTop: 'auto',
          }}
        >
          <div
            style={{
              fontSize: isThermal ? '6px' : isA5 ? '6.5px' : '8px',
              minWidth: 0,
              overflowWrap: 'anywhere',
            }}
          >
            📞 {BILL_FOOTER.phones} &nbsp;|&nbsp; 📧 {BILL_FOOTER.email}
          </div>
          <div
            style={{
              backgroundColor: '#0047AB',
              color: 'white',
              padding: isThermal ? '2px 8px' : isA5 ? '2px 8px' : '3px 9px',
              fontSize: isThermal ? '5.5px' : isA5 ? '6px' : '7px',
              clipPath: 'polygon(12% 0, 100% 0, 100% 100%, 12% 100%, 0 50%)',
              maxWidth: '58%',
              overflowWrap: 'anywhere',
              boxSizing: 'border-box',
            }}
            dir="rtl"
          >
            آدرس: {BILL_FOOTER.address}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrintablePressBill;
